import { FC, FunctionComponent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { withStyles, useForm, BodyText, UploadFile, FieldData, sanitizeToNumeric, TextArea, Select } from 'vopay';
import * as Yup from 'yup';

import Header from '../Header/Header';
import StepIndicator from '../StepIndicator/StepIndicator';
import { DetailsCard, Title, Button } from 'UI';
import { useBillingFieldRenderer } from '../Billing/PackageDetails/utils/renderBillingField';
import { railDisplayLabel, isPermissionAllowedForCurrency } from '../Billing/PackageDetails/utils/planSettingHelpers';
import { BillingData } from '../Billing/Billing';

import styles from './Risk.module.scss';

const cn = withStyles(styles);

type RailRow = { key: string; label: string };
type RiskFormValues = Record<string, string>;
type RiskFormAllValues = Record<string, string | boolean> & {
  flowOfFundsFile: string;
  flowOfFundsFileName: string;
  businessModelCollectingFromThirdParties: boolean;
  businessModelFundingFromOwnAccount: boolean;
  businessModelDistributingToThirdParties: boolean;
  businessModelActingAsIntermediary: boolean;
  businessModelAdditionalContext: string;
};

export type RiskData = {
  riskMatrix: RiskFormValues;
  flowOfFundsFile: string;
  flowOfFundsFileName: string;
  businessModelCollectingFromThirdParties: boolean;
  businessModelFundingFromOwnAccount: boolean;
  businessModelDistributingToThirdParties: boolean;
  businessModelActingAsIntermediary: boolean;
  businessModelAdditionalContext: string;
};

type RiskMatrixProps = {
  collectRails: RailRow[];
  sendRails: RailRow[];
  currency: string;
  steps: string[];
  currentStep: number;
  nextStep: () => void;
  prevStep: () => void;
  onValuesChange: (values: RiskData) => void;
};

const RiskMatrix: FC<RiskMatrixProps> = ({
  collectRails,
  sendRails,
  currency,
  steps,
  currentStep,
  nextStep,
  prevStep,
  onValuesChange,
}) => {
  const { t } = useTranslation('translations', { keyPrefix: 'R1AccountCreation.Risk' });
  const renderBillingField = useBillingFieldRenderer(currency);

  const allRails = useMemo(() => [...collectRails, ...sendRails], [collectRails, sendRails]);

  const frequencyOptions = useMemo(
    () => [
      { value: 'daily', label: t('Frequency.daily', { defaultValue: 'Daily' }) },
      { value: 'monthly', label: t('Frequency.monthly', { defaultValue: 'Monthly' }) },
      { value: 'annually', label: t('Frequency.annually', { defaultValue: 'Annually' }) },
    ],
    [t],
  );

  const initialValues = useMemo<RiskFormAllValues>(
    () => ({
      flowOfFundsFile: '',
      flowOfFundsFileName: '',
      businessModelCollectingFromThirdParties: false,
      businessModelFundingFromOwnAccount: false,
      businessModelDistributingToThirdParties: false,
      businessModelActingAsIntermediary: false,
      businessModelAdditionalContext: '',
      ...Object.fromEntries(
        allRails.flatMap(({ key }) =>
          [`${key}_count`, `${key}_amount`, `${key}_returnChargebackPercent`].map((k) => [k, '']),
        ),
      ),
      ...Object.fromEntries(allRails.map(({ key }) => [`${key}_frequency`, 'monthly'])),
    }),
    [allRails],
  );

  const fields = useMemo<FieldData<RiskFormAllValues>[]>(
    () => [
      {
        fieldName: 'flowOfFundsFile',
        label: () => '',
        getInputState: () => 'optional',
        showField: () => true,
        validate: () =>
          Yup.string().required(t('FlowOfFunds.required', { defaultValue: 'A flow of funds diagram is required.' })),
        serializeField: undefined,
        renderField: ({ getFormikData, ctx }) => {
          const fd = getFormikData<string>({});
          return (
            <UploadFile
              id="flowOfFundsFile"
              name="flowOfFundsFile"
              base64
              keepDataUriPrefix
              keepExtension
              maxMBSize={4}
              value={fd.value}
              valueFileName={ctx.values.flowOfFundsFileName}
              deleteButton
              error={fd.errorMsg}
              onBlur={fd.onBlur}
              onChange={() => {}}
              handleNewFile={(file: string) => {
                ctx.setFieldValue('flowOfFundsFile', file);
                ctx.setFieldTouched('flowOfFundsFile', true);
              }}
              handleFileName={(name: string) => {
                ctx.setFieldValue('flowOfFundsFileName', name);
              }}
            />
          );
        },
      },
      {
        fieldName: 'businessModelCollectingFromThirdParties',
        label: () => '',
        getInputState: () => 'optional',
        showField: () => true,
        validate: () => Yup.boolean(),
        renderField: ({ ctx }) => {
          return (
            <div className={cn('businessModelCheckbox')}>
              <input
                type="checkbox"
                id="businessModelCollectingFromThirdParties"
                checked={Boolean(ctx.values.businessModelCollectingFromThirdParties)}
                onChange={(e) => {
                  ctx.setFieldValue('businessModelCollectingFromThirdParties', e.target.checked);
                }}
              />
              <label htmlFor="businessModelCollectingFromThirdParties">
                {t('BusinessModel.collectingFromThirdParties', { defaultValue: 'Collecting from third parties' })}
              </label>
            </div>
          );
        },
      },
      {
        fieldName: 'businessModelFundingFromOwnAccount',
        label: () => '',
        getInputState: () => 'optional',
        showField: () => true,
        validate: () => Yup.boolean(),
        renderField: ({ ctx }) => {
          return (
            <div className={cn('businessModelCheckbox')}>
              <input
                type="checkbox"
                id="businessModelFundingFromOwnAccount"
                checked={Boolean(ctx.values.businessModelFundingFromOwnAccount)}
                onChange={(e) => {
                  ctx.setFieldValue('businessModelFundingFromOwnAccount', e.target.checked);
                }}
              />
              <label htmlFor="businessModelFundingFromOwnAccount">
                {t('BusinessModel.fundingFromOwnAccount', {
                  defaultValue: 'Funding transactions from its own account',
                })}
              </label>
            </div>
          );
        },
      },
      {
        fieldName: 'businessModelDistributingToThirdParties',
        label: () => '',
        getInputState: () => 'optional',
        showField: () => true,
        validate: () => Yup.boolean(),
        renderField: ({ ctx }) => {
          return (
            <div className={cn('businessModelCheckbox')}>
              <input
                type="checkbox"
                id="businessModelDistributingToThirdParties"
                checked={Boolean(ctx.values.businessModelDistributingToThirdParties)}
                onChange={(e) => {
                  ctx.setFieldValue('businessModelDistributingToThirdParties', e.target.checked);
                }}
              />
              <label htmlFor="businessModelDistributingToThirdParties">
                {t('BusinessModel.distributingToThirdParties', { defaultValue: 'Distributing funds to third parties' })}
              </label>
            </div>
          );
        },
      },
      {
        fieldName: 'businessModelActingAsIntermediary',
        label: () => '',
        getInputState: () => 'optional',
        showField: () => true,
        validate: () => Yup.boolean(),
        renderField: ({ ctx }) => {
          return (
            <div className={cn('businessModelCheckbox')}>
              <input
                type="checkbox"
                id="businessModelActingAsIntermediary"
                checked={Boolean(ctx.values.businessModelActingAsIntermediary)}
                onChange={(e) => {
                  ctx.setFieldValue('businessModelActingAsIntermediary', e.target.checked);
                }}
              />
              <label htmlFor="businessModelActingAsIntermediary">
                {t('BusinessModel.actingAsIntermediary', {
                  defaultValue: 'Acting purely as an intermediary between multiple parties',
                })}
              </label>
            </div>
          );
        },
      },
      {
        fieldName: 'businessModelAdditionalContext',
        label: () => t('BusinessModel.additionalContext', { defaultValue: 'Additional Context' }),
        getInputState: () => 'optional',
        showField: () => true,
        validate: () => Yup.string(),
        renderField: ({ getFormikData }) => {
          const fd = getFormikData({});
          return (
            <TextArea
              name={fd.name}
              value={String(fd.value ?? '')}
              onChange={fd.onChange}
              onBlur={fd.onBlur}
              variant="outlineShadow"
              placeholder={t('BusinessModel.additionalContextPlaceholder', {
                defaultValue: 'We collect from the consumers via EFT and distribute to vendors weekly.',
              })}
              rows={4}
            />
          );
        },
      },
      ...allRails.flatMap(({ key }): FieldData<RiskFormAllValues>[] => [
        {
          fieldName: `${key}_count`,
          label: () => '',
          getInputState: () => 'optional',
          showField: () => true,
          validate: () =>
            Yup.string()
              .required()
              .test(
                'count-non-zero',
                t('Matrix.countNonZero', { defaultValue: 'Transaction count must be greater than 0.' }),
                (value) => Number(sanitizeToNumeric(String(value ?? ''))) > 0,
              ),

          renderField: ({ getFormikData }) => renderBillingField('numeric', getFormikData),
          serializeField: (values) =>
            sanitizeToNumeric(String(values[`${key}_count` as keyof RiskFormAllValues] ?? '')) ?? '',
        } satisfies FieldData<RiskFormAllValues>,
        {
          fieldName: `${key}_amount`,
          label: () => '',
          getInputState: () => 'optional',
          showField: () => true,
          validate: () =>
            Yup.string()
              .required()
              .test(
                'amount-non-zero',
                t('Matrix.amountNonZero', { defaultValue: 'Transaction amount must be greater than 0.' }),
                (value) => Number(sanitizeToNumeric(String(value ?? ''))) > 0,
              ),

          renderField: ({ getFormikData }) => renderBillingField('amount', getFormikData),
          serializeField: (values) =>
            sanitizeToNumeric(String(values[`${key}_amount` as keyof RiskFormAllValues] ?? '')) ?? '',
        } satisfies FieldData<RiskFormAllValues>,
        {
          fieldName: `${key}_frequency`,
          label: () => '',
          getInputState: () => 'optional',
          showField: () => true,
          validate: () => Yup.string().required(),
          renderField: ({ getFormikData }) => {
            const fd = getFormikData<string>({});
            return (
              <div style={{ width: '100%' }}>
                <Select
                  version="2"
                  sort={false}
                  options={frequencyOptions}
                  optionValue={(o) => o.value}
                  optionLabel={(o) => o.label}
                  {...fd}
                />
              </div>
            );
          },
        } satisfies FieldData<RiskFormAllValues>,
        {
          fieldName: `${key}_returnChargebackPercent`,
          label: () => '',
          getInputState: () => 'optional',
          showField: () => true,
          validate: () => Yup.string().required(),

          renderField: ({ getFormikData }) => renderBillingField('percent', getFormikData),
          serializeField: (values) =>
            sanitizeToNumeric(String(values[`${key}_returnChargebackPercent` as keyof RiskFormAllValues] ?? '')) ?? '',
        } satisfies FieldData<RiskFormAllValues>,
      ]),
    ],
    [allRails, renderBillingField, frequencyOptions, t],
  );

  const { ctx, renderFormElements } = useForm<RiskFormAllValues>({
    config: fields,
    initialValues,
    onSubmit: () => nextStep(),
  });

  useEffect(() => {
    const serialized = Object.fromEntries(
      fields.map((f) => [
        f.fieldName,
        f.serializeField ? (f.serializeField(ctx.values) ?? '') : ctx.values[f.fieldName],
      ]),
    );
    const riskMatrixEntries = Object.entries(serialized).filter(
      ([key]) =>
        ![
          'flowOfFundsFile',
          'flowOfFundsFileName',
          'businessModelCollectingFromThirdParties',
          'businessModelFundingFromOwnAccount',
          'businessModelDistributingToThirdParties',
          'businessModelActingAsIntermediary',
          'businessModelAdditionalContext',
        ].includes(key),
    );
    onValuesChange({
      riskMatrix: Object.fromEntries(riskMatrixEntries) as RiskFormValues,
      flowOfFundsFile: String(serialized.flowOfFundsFile ?? ''),
      flowOfFundsFileName: String(ctx.values.flowOfFundsFileName ?? ''),
      businessModelCollectingFromThirdParties: Boolean(ctx.values.businessModelCollectingFromThirdParties),
      businessModelFundingFromOwnAccount: Boolean(ctx.values.businessModelFundingFromOwnAccount),
      businessModelDistributingToThirdParties: Boolean(ctx.values.businessModelDistributingToThirdParties),
      businessModelActingAsIntermediary: Boolean(ctx.values.businessModelActingAsIntermediary),
      businessModelAdditionalContext: String(ctx.values.businessModelAdditionalContext ?? ''),
    });
  }, [ctx.values, fields]);

  const renderTable = (sectionTitle: string, rails: RailRow[]) => {
    if (rails.length === 0) return null;

    return (
      <DetailsCard className={cn('tableCard')}>
        <p className={cn('sectionTitle')}>
          {sectionTitle} <span className={cn('requiredMark')}>*</span>
        </p>
        <div className={cn('tableContainer')}>
          <table>
            <thead>
              <tr>
                <th>{t('Columns.paymentRail', { defaultValue: 'Payment Rail' })}</th>
                <th style={{ width: '20%' }}>{t('Columns.transactionCount', { defaultValue: 'Transaction Count' })}</th>
                <th style={{ width: '22%' }}>
                  {t('Columns.transactionAmount', { defaultValue: 'Transaction Amount' })}
                </th>
                <th style={{ width: '18%' }}>
                  {t('Columns.returnChargeback', { defaultValue: 'Return/Chargeback %' })}
                </th>
                <th style={{ width: '18%' }}>{t('Columns.frequency', { defaultValue: 'Frequency' })}</th>
              </tr>
            </thead>
            <tbody>
              {rails.map(({ key, label }) => (
                <tr key={key}>
                  <td className={cn('railLabel')}>
                    {label} <span className={cn('requiredMark')}>*</span>
                  </td>
                  <td>{renderFormElements({ config: fields.filter((f) => f.fieldName === `${key}_count`) })}</td>
                  <td>{renderFormElements({ config: fields.filter((f) => f.fieldName === `${key}_amount`) })}</td>
                  <td>
                    {renderFormElements({
                      config: fields.filter((f) => f.fieldName === `${key}_returnChargebackPercent`),
                    })}
                  </td>
                  <td>{renderFormElements({ config: fields.filter((f) => f.fieldName === `${key}_frequency`) })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DetailsCard>
    );
  };

  return (
    <form className={cn('container')} onSubmit={ctx.handleSubmit}>
      <div className={cn('stickyHeader')}>
        <Header
          header={t('title', { defaultValue: 'Risk' })}
          description={t('description', {
            defaultValue: 'This information is reviewed to assess funds associated risk. Please fill it carefully.',
          })}
          rightArea={<StepIndicator steps={steps} activeStep={currentStep} />}
        />
      </div>
      <div className={cn('body')}>
        <DetailsCard className={cn('outerCard')}>
          <div className={cn('cardHeader')}>
            <Title level="5">
              {t('FlowOfFunds.title', { defaultValue: 'Flow of Funds Diagram' })}{' '}
              <span className={cn('requiredMark')}>*</span>
            </Title>
            <BodyText className={cn('cardDescription')}>
              {t('FlowOfFunds.description', {
                defaultValue:
                  'Your diagram should clearly show: who initiates payments, how funds enter the VoPay account, how funds leave the VoPay account, and who the end recipients are.',
              })}
            </BodyText>
          </div>
          <div>{renderFormElements({ config: fields.filter((f) => f.fieldName === 'flowOfFundsFile') })}</div>
        </DetailsCard>
        <DetailsCard className={cn('outerCard')}>
          <div className={cn('cardHeader')}>
            <Title level="5">
              {t('BusinessModel.title', { defaultValue: 'Business Model Type' })}{' '}
              <span className={cn('requiredMark')}>*</span>
            </Title>
            <BodyText className={cn('cardDescription')}>
              {t('BusinessModel.description', {
                defaultValue: 'Select all options that describe how this client moves funds through VoPay.',
              })}
            </BodyText>
          </div>
          <div className={cn('businessModelSection')}>
            {renderFormElements({
              config: fields.filter((f) =>
                [
                  'businessModelCollectingFromThirdParties',
                  'businessModelFundingFromOwnAccount',
                  'businessModelDistributingToThirdParties',
                  'businessModelActingAsIntermediary',
                ].includes(f.fieldName),
              ),
            })}
          </div>
          <div className={cn('businessModelAdditionalContext')}>
            {renderFormElements({
              config: fields.filter((f) => f.fieldName === 'businessModelAdditionalContext'),
            })}
          </div>
        </DetailsCard>

        <DetailsCard className={cn('outerCard')}>
          <div className={cn('cardHeader')}>
            <Title level="5">{t('riskMatrixTitle', { defaultValue: 'Risk Matrix' })}</Title>
            <BodyText className={cn('cardDescription')}>
              {t('riskMatrixDescription', {
                defaultValue:
                  'The following details you provide will be reviewed by a designated sales manager to determine risks.',
              })}
            </BodyText>
          </div>

          {renderTable(t('collectSection', { defaultValue: 'Funds collected from third parties' }), collectRails)}
          {renderTable(t('sendSection', { defaultValue: 'Funds sent to third parties' }), sendRails)}
        </DetailsCard>
      </div>

      <div className={cn('footer')}>
        <Button colorScheme="secondary" type="button" onClick={prevStep}>
          {t('back', { defaultValue: 'Back' })}
        </Button>
        <Button colorScheme="primary" disabled={!ctx.isValid} type="submit">
          {t('next', { defaultValue: 'Next' })}
        </Button>
      </div>
    </form>
  );
};

type RiskProps = {
  steps: string[];
  currentStep: number;
  nextStep: () => void;
  prevStep: () => void;
  billingData: BillingData | null;
  onValuesChange: (values: RiskData) => void;
};

const Risk: FunctionComponent<RiskProps> = ({
  steps,
  currentStep,
  nextStep,
  prevStep,
  billingData,
  onValuesChange,
}) => {
  const currency = useMemo(() => {
    const agreementDetails = billingData?.billingPackageInfo?.AgreementDetails;
    if (agreementDetails) {
      const currencyEntry = Object.entries(agreementDetails).find(([key]) => key === 'Currency');
      const settingsKey = currencyEntry?.[1].settings;
      if (settingsKey) {
        const userCurrency = billingData?.packageDetails?.agreementDetails?.[settingsKey];
        if (typeof userCurrency === 'string' && userCurrency) return userCurrency;
      }
    }
    return billingData?.billingPackageInfo?.BillingSettings?.Currency.value ?? 'CAD';
  }, [billingData]);

  const collectRails = useMemo<RailRow[]>(() => {
    const rails = billingData?.billingPackageInfo?.PaymentRails;
    const userValues = billingData?.packageDetails?.paymentRails;
    if (!rails) return [];
    return Object.entries(rails)
      .filter(
        ([, ps]) =>
          ps.flowType === 'collect' &&
          Object.keys(ps.permissions).some(
            (k) => userValues?.[k] === true && isPermissionAllowedForCurrency(k, currency),
          ),
      )
      .map(([displayName, ps]) => ({
        key: Object.keys(ps.permissions).find((k) => isPermissionAllowedForCurrency(k, currency)) ?? displayName,
        label: railDisplayLabel(displayName, currency),
      }));
  }, [billingData, currency]);

  const sendRails = useMemo<RailRow[]>(() => {
    const rails = billingData?.billingPackageInfo?.PaymentRails;
    const userValues = billingData?.packageDetails?.paymentRails;
    if (!rails) return [];
    return Object.entries(rails)
      .filter(
        ([, ps]) =>
          ps.flowType === 'send' &&
          Object.keys(ps.permissions).some(
            (k) => userValues?.[k] === true && isPermissionAllowedForCurrency(k, currency),
          ),
      )
      .map(([displayName, ps]) => ({
        key: Object.keys(ps.permissions).find((k) => isPermissionAllowedForCurrency(k, currency)) ?? displayName,
        label: railDisplayLabel(displayName, currency),
      }));
  }, [billingData, currency]);

  return (
    <RiskMatrix
      key={JSON.stringify({ collectRails, sendRails })}
      collectRails={collectRails}
      sendRails={sendRails}
      currency={currency}
      steps={steps}
      currentStep={currentStep}
      nextStep={nextStep}
      prevStep={prevStep}
      onValuesChange={onValuesChange}
    />
  );
};

export default Risk;
